/**
 * ResourceSelectors.tsx
 *
 * Three-column layout for the Quick Tests wizard.
 * Left:   Cluster/Location selector with tabs
 * Middle: eSIM selector — a group, ONE specific SIM, or a granted shared pool
 *         (encoding in utils/esimSelection.ts). Also computes the placement
 *         verdict that blocks a SIM which can't serve the selected location.
 * Right:  Network constraints (APN override, forced RAT, forced PLMN)
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Layers, MapPin, CreditCard, Radio, Check, AlertTriangle, Plus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { HierarchicalLocationSelector } from '@/components/locations/HierarchicalLocationSelector';
import { getClustersForOrg, calculateClusterStats } from '@/services/clusterService';
import { getRegions, getCountries, getLocations, filterLocationsByTenancy } from '@/services/hierarchicalLocationService';
import { getGroupsForOrg, getProfilesForOrg, getSharedProfiles, sharedGroupsForOrg } from '@/services/esimService';
import { DEFAULT_SHARED_ESIM_GROUP_ID, canOrgUseSharedEsims, isProfileReinstallable } from '@simcheck/shared-types';
import { ESimSelectionCombobox } from './ESimSelectionCombobox';
import { parseESimSelection, resolveESimPlacement } from '@/utils/esimSelection';
import { edgeDeviceService } from '@/services/edgeDeviceService';
import type { Cluster, Region, Country, Location, ESimGroup, ESimProfile, ForcedRat, ModemModel } from '@simcheck/shared-types';
import { RatSelector } from './RatSelector';
import {
  availableRatsAtLocation,
  availableRatsForCountry,
  effectiveModemCaps,
  type RatAvailabilityForCountry,
} from '@/utils/ratCapability';
import { getAllModemModels } from '@/services/modemModelService';

export interface ResourceSelectorsProps {
  selectionMode: 'individual' | 'cluster';
  onSelectionModeChange: (mode: 'individual' | 'cluster') => void;
  selectedClusterId: string | null;
  onClusterChange: (clusterId: string | null) => void;
  selectedLocationIds: string[];
  onLocationIdsChange: (locationIds: string[]) => void;
  /**
   * Encoded eSIM selection — an org group id, `profile:<id>`, or
   * `shared:<sentinel>`. See utils/esimSelection.ts.
   */
  selectedESimSelection: string | null;
  onESimSelectionChange: (value: string | null) => void;
  selectedNetworkMccMnc: string | null;
  onNetworkChange: (mccMnc: string | null) => void;
  selectedRat: ForcedRat;
  onRatChange: (rat: ForcedRat) => void;
  /**
   * Data-bearer APN OVERRIDE (optional since issue #161). Empty = use the
   * APN configured on the eSIM (profile → group → network default). Shared
   * across every test + TLAM.
   */
  apn: string;
  onApnChange: (apn: string) => void;
  /**
   * Reports per-modem effective capabilities for the online modems at the
   * selected location(s) so the parent can gray out tests with no viable
   * modem under the forced network type. `null` = not yet known (no location
   * selected, still loading, or load failed) → parent should not gray anything.
   */
  onModemCapsChange?: (caps: Array<Record<string, boolean>> | null) => void;
  /**
   * Reports why the selected specific SIM can't serve the selected location(s)
   * — e.g. a shared/resident SIM that lives elsewhere, or a consumed single-use
   * code. Non-null means the backend would hard-fail the run, so the parent
   * should block launching. `null` = nothing wrong.
   */
  onESimBlockedChange?: (reason: string | null) => void;
  /**
   * Reports the IMEI of the modem a **pinned** SIM (a shared/resident SIM) is
   * locked to, or `null` when the selection isn't pinned. When set, the RAT
   * options + reported modem caps are narrowed to that one modem, and the
   * parent grays test cards it can't run even on Automatic (there's no other
   * modem to fall back to).
   */
  onPinnedModemChange?: (imei: string | null) => void;
  /**
   * The selected network's option text, e.g. "AT&T (310/410)", so the run can
   * record exactly what was picked. null when Automatic or not in the list.
   */
  onNetworkLabelChange?: (label: string | null) => void;
}

export function ResourceSelectors({
  selectionMode,
  onSelectionModeChange,
  selectedClusterId,
  onClusterChange,
  selectedLocationIds,
  onLocationIdsChange,
  selectedESimSelection,
  onESimSelectionChange,
  selectedNetworkMccMnc,
  onNetworkChange,
  selectedRat,
  onRatChange,
  apn,
  onApnChange,
  onModemCapsChange,
  onESimBlockedChange,
  onPinnedModemChange,
  onNetworkLabelChange,
}: ResourceSelectorsProps) {
  const { activeOrganization, isSuperAdmin } = useAuth();

  // State for clusters and hierarchy data
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loadingClusters, setLoadingClusters] = useState(false);
  const [availableNetworks, setAvailableNetworks] = useState<Array<{ operator_name: string; mcc: string; mnc: string; network_generation: string }>>([]);
  const [loadingNetworks, setLoadingNetworks] = useState(false);
  // Set of RATs that at least one online modem at the selected location supports.
  // `null` while loading → RatSelector keeps all options enabled (avoids flicker).
  const [availableRats, setAvailableRats] = useState<Set<Exclude<ForcedRat, 'auto'>> | null>(null);
  // Per-RAT availability map ("supported at N of M locations"). Populated
  // when the user multi-selects locations (e.g. via the location selector's
  // "Select all in <country>" action). With a single location selected we
  // leave it null and RatSelector falls back to the per-location caption.
  const [availabilityByRat, setAvailabilityByRat] = useState<Map<
    Exclude<ForcedRat, 'auto'>,
    RatAvailabilityForCountry
  > | null>(null);
  // Curated modem-model capabilities. Loaded once on mount; needed because
  // capabilities live on the model doc, not on the per-modem record.
  const [modemModels, setModemModels] = useState<ModemModel[]>([]);
  // Online modems at the selected location(s). `null` = not yet loaded (keeps
  // the RAT selector all-enabled during load instead of flashing disabled). The
  // RAT/caps derivation below reads this so it can re-narrow to a pinned holder
  // modem when the eSIM selection changes without a full network reload.
  const [modemsAtLocations, setModemsAtLocations] = useState<any[] | null>(null);
  // deviceId → locationId, for resolving where a specific SIM currently lives.
  const [deviceLocations, setDeviceLocations] = useState<Map<string, string | null>>(new Map());
  const [locationModemCounts, setLocationModemCounts] = useState<Map<string, number>>(new Map());

  // Live view of the forced-network selection for the async network load below.
  // The load effect must not depend on `selectedNetworkMccMnc` (that would
  // re-fetch every scan on a dropdown change), so it reads through this ref.
  const selectedNetworkRef = useRef(selectedNetworkMccMnc);
  selectedNetworkRef.current = selectedNetworkMccMnc;

  // State for eSIM groups
  const [eSimGroups, setESimGroups] = useState<ESimGroup[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  // Org inventory, so the picker can also bind a run to ONE specific SIM.
  // Loaded here (not derived from the selected group) because `groupId` is
  // optional on a profile — ungrouped SIMs must stay reachable.
  const [orgProfiles, setOrgProfiles] = useState<ESimProfile[]>([]);
  // Super-admin shared eSIM inventory (granted orgs only). Selected via the
  // picker with a `shared:<sentinel>` value the parent decodes.
  const [sharedProfiles, setSharedProfiles] = useState<ESimProfile[]>([]);

  // Load clusters and location data
  useEffect(() => {
    if (!activeOrganization) return;

    const loadData = async () => {
      setLoadingClusters(true);
      try {
        const [clustersData, regionsData, countriesData, locationsData] = await Promise.all([
          getClustersForOrg(activeOrganization.id),
          getRegions(),
          getCountries(),
          getLocations(),
        ]);
        setClusters(clustersData);
        setRegions(regionsData);
        setCountries(countriesData);
        // Tenancy (#202): never surface other orgs' private locations.
        setLocations(filterLocationsByTenancy(locationsData, activeOrganization.id, isSuperAdmin));
      } catch (err) {
        console.error('Error loading clusters:', err);
      } finally {
        setLoadingClusters(false);
      }
    };

    loadData();
  }, [activeOrganization]);

  // Load curated modem-model capabilities once on mount.
  useEffect(() => {
    getAllModemModels(true).then(setModemModels).catch(err => {
      console.error('Error loading modem models:', err);
    });
  }, []);

  // deviceId → locationId for EVERY device, so a specific SIM's `installedOn`
  // pointer can be resolved to the location it physically lives at (the
  // networks effect below only sees devices at the *selected* locations).
  useEffect(() => {
    edgeDeviceService.getDevices()
      .then(devices => {
        setDeviceLocations(new Map(devices.map(d => [d.id, d.locationId || null])));
        // Live modem count per location. The `modemCount` field on the location
        // document is stale (commonly 0), which made a populated cluster report
        // "0 modems" in the picker.
        const counts = new Map<string, number>();
        for (const d of devices) {
          if (!d.locationId) continue;
          counts.set(d.locationId, (counts.get(d.locationId) || 0) + (d.modems?.length || 0));
        }
        setLocationModemCounts(counts);
      })
      .catch(err => {
        console.error('Error loading devices for eSIM residency:', err);
      });
  }, []);

  // Locations carrying live modem counts — used for cluster stats only, so the
  // set of selectable locations is untouched.
  const locationsWithLiveModems = useMemo(
    () =>
      locations.map(l => ({
        ...l,
        modemCount: locationModemCounts.get(l.id) ?? l.modemCount ?? 0,
      })),
    [locations, locationModemCounts],
  );

  // Load eSIM groups
  useEffect(() => {
    if (!activeOrganization) return;

    const loadGroups = async () => {
      setLoadingGroups(true);
      try {
        // Independent fetches, each non-fatal: groups are OPTIONAL. A tenant
        // that never created one can still run on a single loose profile or a
        // granted shared pool, so a groups failure must not take the profile
        // list down with it (that would show "No eSIMs yet" to someone who has
        // SIMs) — and vice versa.
        const [groupsData, profilesData] = await Promise.all([
          getGroupsForOrg(activeOrganization.id).catch(() => [] as ESimGroup[]),
          getProfilesForOrg(activeOrganization.id).catch(() => [] as ESimProfile[]),
        ]);
        setESimGroups(groupsData);
        setOrgProfiles(profilesData);
        if (canOrgUseSharedEsims(activeOrganization as any)) {
          const shared = await getSharedProfiles().catch(() => []);
          // Keep `in_use` / `downloaded` members: the picker shows them with a
          // note and the queue waits for the holder. Dropping them made a pool's
          // count shrink mid-run and the SIM disappear from the list.
          setSharedProfiles(
            shared.filter((p) => p.status !== 'expired' && p.status !== 'disabled'),
          );
        } else {
          setSharedProfiles([]);
        }
      } catch (err) {
        console.error('Error loading eSIM inventory:', err);
      } finally {
        setLoadingGroups(false);
      }
    };

    loadGroups();
  }, [activeOrganization]);

  // Load available networks AND modem capabilities (for RAT selector) from
  // online modems at every selected location. When multiple locations are
  // picked we walk all of them so the network/RAT pickers union the choices.
  // The RAT selector also gets a per-RAT availability map so it can render
  // "supported at 2 of 4 locations".
  useEffect(() => {
    if (selectedLocationIds.length === 0) {
      setAvailableNetworks([]);
      setAvailabilityByRat(null);
      setModemsAtLocations(null);
      return;
    }

    const loadNetworks = async () => {
      setLoadingNetworks(true);
      try {
        const allDevices = await edgeDeviceService.getDevices();
        const targetLocationIds = new Set(selectedLocationIds);
        const devicesAtLocations = allDevices.filter(
          d => d.locationId && targetLocationIds.has(d.locationId) && d.status === 'online'
        );

        // Pre-seed map keys with every location so the denominator is the
        // user's full selection (not just locations that returned modems).
        const modemsByLocation = new Map<string, any[]>();
        for (const id of selectedLocationIds) modemsByLocation.set(id, []);

        // Get modems from each device (includes availableNetworks from scans).
        // Parallelise to avoid serial waterfall when many edges are picked.
        const networkMap = new Map<string, { operator_name: string; mcc: string; mnc: string; network_generation: string }>();
        const allModems: any[] = [];
        const perDevice = await Promise.all(
          devicesAtLocations.map(async device => {
            const modems = await edgeDeviceService.getModems(device.id);
            return { locationId: device.locationId!, modems };
          })
        );
        for (const { locationId, modems: rawModems } of perDevice) {
          // Mirror the allocator's admin gates: a disabled modem or the
          // device's backhaul modem is never handed out, so it must not
          // widen the RAT picker / capability verdict either.
          const modems = rawModems.filter(
            (m: any) => m.disabled !== true && m.backhaul !== true,
          );
          const bucket = modemsByLocation.get(locationId);
          if (bucket) bucket.push(...modems);
          for (const modem of modems) {
            allModems.push(modem);
            // A managed (persistent / hunt) modem can run plain tests, but it
            // refuses a forced PLMN — its scan results must not widen the
            // "force network" options (#252).
            if ((modem as any).connectivityMode) continue;
            const nets = (modem as any).availableNetworks || [];
            for (const net of nets) {
              if (net.mcc && net.mnc) {
                const key = `${net.mcc}${net.mnc}`;
                if (!networkMap.has(key)) {
                  networkMap.set(key, {
                    operator_name: net.operator_name || `${net.mcc}/${net.mnc}`,
                    mcc: net.mcc,
                    mnc: net.mnc,
                    network_generation: net.network_generation || '',
                  });
                }
              }
            }
          }
        }

        setAvailableNetworks(
          Array.from(networkMap.values()).sort((a, b) => a.operator_name.localeCompare(b.operator_name))
        );
        // Drop a forced-network pick the selected location(s) can't offer — a
        // persisted selection survives location changes, and the native select
        // can't render a value with no matching option, so it silently shows
        // "Automatic" while the stale MCCMNC still rides into every run (seen
        // live: Partner IL 42501 forced on San Francisco runs). Only clear on a
        // successful load; an error must not wipe a valid selection.
        if (selectedNetworkRef.current && !networkMap.has(selectedNetworkRef.current)) {
          onNetworkChange(null);
        }
        // Raw online-modem set → the RAT/caps derivation effect below turns this
        // into `availableRats` + reported caps, narrowing to the pinned holder
        // modem when a shared SIM is selected.
        setModemsAtLocations(allModems);
        // Per-RAT coverage map (only meaningful with multiple locations).
        if (selectedLocationIds.length > 1) {
          setAvailabilityByRat(availableRatsForCountry(modemsByLocation, modemModels));
        } else {
          setAvailabilityByRat(null);
        }
      } catch (err) {
        console.error('Error loading networks from modem scans:', err);
        setAvailableNetworks([]);
        setAvailabilityByRat(null);
        setModemsAtLocations(null);
      } finally {
        setLoadingNetworks(false);
      }
    };

    loadNetworks();
  }, [selectedLocationIds, modemModels]);

  // Calculate cluster stats
  const selectedClusterStats = useMemo(() => {
    if (!selectedClusterId) return null;
    const cluster = clusters.find((c) => c.id === selectedClusterId);
    if (!cluster) return null;
    return calculateClusterStats(cluster.items, regions, countries, locationsWithLiveModems);
  }, [selectedClusterId, clusters, regions, countries, locationsWithLiveModems]);

  const selectedCluster = clusters.find((c) => c.id === selectedClusterId);

  // Where a SIM physically lives right now. Shared SIMs are pinned to their
  // holder modem, so this decides which of them can serve the run at all; for
  // org SIMs it only decides ordering (they re-download).
  const residencyOf = useCallback(
    (profile: ESimProfile) => {
      const deviceId = profile.installedOn?.deviceId;
      if (!deviceId) return null;
      const locationId = deviceLocations.get(deviceId) ?? null;
      return {
        locationId,
        locationName: (locationId && locations.find((l) => l.id === locationId)?.name) || null,
      };
    },
    [deviceLocations, locations],
  );

  // Shared pools this org is granted, and their members. Both filtered by the
  // same per-group grant the allocator enforces (`canUseSharedGroup`).
  const grantedSharedGroups = useMemo(
    () => sharedGroupsForOrg(sharedProfiles, activeOrganization),
    [sharedProfiles, activeOrganization],
  );
  const grantedSharedProfiles = useMemo(() => {
    const granted = new Set(grantedSharedGroups.map((g) => g.groupId));
    return sharedProfiles.filter((p) =>
      granted.has(p.sharedGroupId || DEFAULT_SHARED_ESIM_GROUP_ID),
    );
  }, [sharedProfiles, grantedSharedGroups]);

  // Decode the step-2 selection into the group / specific-SIM it points at.
  const esimSelection = parseESimSelection(selectedESimSelection);
  const selectedESimGroup = eSimGroups.find((g) => g.id === esimSelection.groupId);
  // A specific SIM can be org inventory OR one exact member of a shared pool
  // (`resolveESimPlacement` knows the difference and is stricter about shared).
  const selectedProfile = esimSelection.isSharedPool
    ? undefined
    : orgProfiles.find((p) => p.id === esimSelection.profileId) ||
      grantedSharedProfiles.find((p) => p.id === esimSelection.profileId);
  // A SIM lives on exactly one eUICC, so binding a run to a specific one is
  // constrained by where it currently is and whether it can be re-downloaded.
  // Mirror the backend's placement rules here so an impossible combination is
  // caught before submit rather than hard-failing at dispatch.
  // Holder IMEI → modem model name, resolved from the online modems loaded for
  // the selected location(s). Lets the placement message show the model next to
  // the IMEI. Returns null when the holder isn't in the loaded set (offline /
  // elsewhere) → the message falls back to the IMEI alone.
  const modemModelOf = useCallback(
    (imei: string): string | null => {
      const m = (modemsAtLocations || []).find((x: any) => x.imei === imei);
      return m?.modemModelName || m?.model || null;
    },
    [modemsAtLocations],
  );
  // Network types the holder modem supports (null when it isn't among the
  // loaded modems), so the placement hint knows when the selected network type
  // forces the SIM off its holder (#381).
  const holderRatsOf = useCallback(
    (imei: string): Set<string> | null => {
      const m = (modemsAtLocations || []).find((x: any) => x.imei === imei);
      return m ? availableRatsAtLocation([m], modemModels) : null;
    },
    [modemsAtLocations, modemModels],
  );
  const placement = useMemo(() => {
    if (!selectedProfile) return null;
    return resolveESimPlacement({
      profile: selectedProfile,
      selectedLocationIds,
      deviceLocationOf: (deviceId) => deviceLocations.get(deviceId) ?? null,
      locationNameOf: (locationId) =>
        (locationId && locations.find((l) => l.id === locationId)?.name) || 'an unknown location',
      modemModelOf,
      forcedRat: selectedRat,
      holderRatsOf,
    });
  }, [selectedProfile, selectedLocationIds, deviceLocations, locations, modemModelOf, selectedRat, holderRatsOf]);

  // Let the parent gate the test tiles on an impossible SIM/location pairing.
  useEffect(() => {
    onESimBlockedChange?.(placement?.blocking ? placement.message : null);
  }, [placement, onESimBlockedChange]);

  // A shared SIM is hard-pinned to the exact modem holding it, so a run using it
  // inherits THAT modem's capabilities — not the location's union. Org SIMs
  // re-download to any modem, so they stay location-wide (not pinned).
  const pinnedHolderImei =
    selectedProfile?.shared
      ? (placement?.holderImei ?? selectedProfile?.installedOn?.imei ?? null)
      : null;

  // Tell the parent the selected network's option text (same as the <option>).
  useEffect(() => {
    const net = selectedNetworkMccMnc
      ? availableNetworks.find((n) => `${n.mcc}${n.mnc}` === selectedNetworkMccMnc)
      : undefined;
    onNetworkLabelChange?.(net ? `${net.operator_name} (${net.mcc}/${net.mnc})` : null);
  }, [selectedNetworkMccMnc, availableNetworks, onNetworkLabelChange]);

  // Tell the parent which modem is pinned so it can gray test cards the holder
  // can't run even on Automatic (there's no other modem to fall back to).
  useEffect(() => {
    onPinnedModemChange?.(pinnedHolderImei);
  }, [pinnedHolderImei, onPinnedModemChange]);

  // Derive the RAT visibility set + reported modem caps from the online modems
  // at the selected location(s) — narrowed to just the pinned holder modem when
  // a shared SIM is selected. Kept separate from the network load so re-picking
  // the eSIM re-narrows without a full modem refetch.
  useEffect(() => {
    if (selectedLocationIds.length === 0 || modemsAtLocations === null) {
      setAvailableRats(null);
      onModemCapsChange?.(null);
      return;
    }
    let effModems: any[] = modemsAtLocations;
    if (pinnedHolderImei) {
      const holder = modemsAtLocations.find((m: any) => m.imei === pinnedHolderImei);
      // Holder not among the online modems here (offline / wrong location) →
      // nothing is runnable; leave the set empty so every option grays out. The
      // placement verdict already blocks launching in that case.
      effModems = holder ? [holder] : [];
    }
    // Managed (persistent / hunt) modems refuse a forced RAT, so they don't
    // widen the network-type options — but they still count for test-card
    // viability under "Automatic" (#252).
    const ratModems = effModems.filter((m: any) => !m.connectivityMode);
    setAvailableRats(availableRatsAtLocation(ratModems, modemModels));
    onModemCapsChange?.(effectiveModemCaps(effModems, modemModels));
  }, [modemsAtLocations, modemModels, pinnedHolderImei, selectedLocationIds, onModemCapsChange]);
  // APN actually in effect for the selection (profile wins over group).
  const inheritedApn = selectedProfile?.apn || selectedESimGroup?.apn || null;
  const inheritedApnSource = selectedProfile?.apn
    ? `eSIM "${selectedProfile.name || selectedProfile.iccid}"`
    : selectedESimGroup?.apn
      ? `eSIM group "${selectedESimGroup.name}"`
      : null;

  const handleModeChange = (value: string) => {
    const mode = value as 'individual' | 'cluster';
    onSelectionModeChange(mode);
    // Clear selections when switching modes
    onClusterChange(null);
    onLocationIdsChange([]);
  };

  const hasLocationSelected =
    (selectionMode === 'cluster' && !!selectedClusterId) ||
    (selectionMode === 'individual' && selectedLocationIds.length > 0);
  const hasESimSelected = !!selectedESimSelection;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Left Column: Step 1 — Cluster/Location Selector */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <div className={`flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold ${hasLocationSelected ? 'bg-emerald-600 text-white' : 'bg-primary text-primary-foreground'}`}>
            {hasLocationSelected ? <Check className="w-4 h-4" /> : '1'}
          </div>
          <Label className="text-base font-semibold text-foreground">Select Test Locations</Label>
        </div>

        <Tabs value={selectionMode} onValueChange={handleModeChange}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="individual" className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Individual Locations
            </TabsTrigger>
            <TabsTrigger value="cluster" className="flex items-center gap-2">
              <Layers className="w-4 h-4" />
              Cluster
            </TabsTrigger>
          </TabsList>

          <TabsContent value="individual" className="mt-3 space-y-3">
            <div className="space-y-2">
              <Label htmlFor="location-selector" className="text-sm text-muted-foreground">
                Select Locations
              </Label>
              <HierarchicalLocationSelector
                mode="multi"
                selectedLocationIds={selectedLocationIds}
                onSelectLocations={onLocationIdsChange}
              />
            </div>
          </TabsContent>

          <TabsContent value="cluster" className="mt-3 space-y-3">
            {!loadingClusters && clusters.length === 0 ? (
              <div className="flex flex-col items-center gap-3 p-6 border border-dashed border-border rounded-lg text-center">
                <Layers className="w-8 h-8 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-foreground">No clusters yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Group your locations into clusters for parallel testing.
                  </p>
                </div>
                <Link
                  to="/clusters"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                >
                  <Plus className="w-4 h-4" />
                  Create your first cluster
                </Link>
              </div>
            ) : (
            <div className="space-y-2">
              <Label htmlFor="cluster-select" className="text-sm text-muted-foreground">
                Select Cluster
              </Label>
              {/* shadcn Select, not a native <select>: macOS renders a native
                  popup at OS scale, which ignores the app's typography and
                  came out enormous on a hi-dpi display. */}
              <Select
                value={selectedClusterId || ''}
                onValueChange={(value) => onClusterChange(value || null)}
                disabled={loadingClusters}
              >
                <SelectTrigger id="cluster-select" className="w-full">
                  <SelectValue placeholder="Select a cluster..." />
                </SelectTrigger>
                <SelectContent>
                  {clusters.map((cluster) => {
                    const stats = calculateClusterStats(
                      cluster.items,
                      regions,
                      countries,
                      locationsWithLiveModems,
                    );
                    return (
                      <SelectItem key={cluster.id} value={cluster.id}>
                        {cluster.name} ({stats.locationCount} location
                        {stats.locationCount === 1 ? '' : 's'})
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            )}

            {/* Cluster stats */}
            {selectedCluster && selectedClusterStats && (
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <div className="font-medium text-foreground">{selectedCluster.name}</div>
                {selectedCluster.description && (
                  <div className="text-sm text-muted-foreground">{selectedCluster.description}</div>
                )}
                <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                  <div>
                    <div className="text-muted-foreground">Locations</div>
                    <div className="font-semibold text-foreground">{selectedClusterStats.locationCount}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Modems</div>
                    <div className="font-semibold text-foreground">{selectedClusterStats.modemCount}</div>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Right Column: Step 2 — eSIM Selector (group OR specific SIM) */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <div className={`flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold ${hasESimSelected ? 'bg-emerald-600 text-white' : 'bg-primary text-primary-foreground'}`}>
            {hasESimSelected ? <Check className="w-4 h-4" /> : '2'}
          </div>
          <div className="flex flex-col">
            <Label className="text-base font-semibold text-foreground">Select eSIM</Label>
            <span className="text-xs text-muted-foreground">
              Pick one specific SIM, or a group to let the allocator choose
            </span>
          </div>
        </div>

        {!loadingGroups && eSimGroups.length === 0 && orgProfiles.length === 0 && sharedProfiles.length === 0 ? (
          <div className="flex flex-col items-center gap-3 p-6 border border-dashed border-border rounded-lg text-center">
            <CreditCard className="w-8 h-8 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium text-foreground">No eSIMs yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Add eSIM profiles to your inventory to run tests.
              </p>
            </div>
            <Link
              to="/esim-inventory"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
            >
              <Plus className="w-4 h-4" />
              Add your first eSIM
            </Link>
          </div>
        ) : (
          <ESimSelectionCombobox
            value={selectedESimSelection}
            onChange={onESimSelectionChange}
            groups={eSimGroups}
            profiles={orgProfiles}
            sharedGroups={grantedSharedGroups}
            sharedProfiles={grantedSharedProfiles}
            selectedLocationIds={selectedLocationIds}
            selectedLocationLabel={
              selectedLocationIds.length === 1
                ? locations.find((l) => l.id === selectedLocationIds[0])?.name || null
                : null
            }
            residencyOf={residencyOf}
            loading={loadingGroups}
            profileNote={(profile) => {
              const residency = residencyOf(profile);
              if (!residency) {
                // Not resident anywhere — flag a code that can't be downloaded again.
                return isProfileReinstallable(profile) ? null : 'code spent';
              }
              return residency.locationName ? `at ${residency.locationName}` : 'installed';
            }}
          />
        )}

        {/* Selected group — the allocator picks any available member */}
        {selectedESimGroup && (
          <div className="p-4 bg-muted rounded-lg space-y-2">
            <div className="font-medium text-foreground">{selectedESimGroup.name}</div>
            {selectedESimGroup.description && (
              <div className="text-sm text-muted-foreground">{selectedESimGroup.description}</div>
            )}
            <div className="mt-3 text-sm">
              <div className="text-muted-foreground">Available Profiles</div>
              <div className="font-semibold text-foreground">{selectedESimGroup.profileCount || 0}</div>
            </div>
            {(selectedESimGroup.profileCount || 0) === 0 && (
              <div className="flex items-center gap-2 mt-2 text-sm text-amber-500">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>This group has no profiles — tests cannot run.</span>
              </div>
            )}
          </div>
        )}

        {/* Selected specific SIM — runs are bound to this exact profile */}
        {selectedProfile && (
          <div className="p-4 bg-muted rounded-lg space-y-2">
            <div className="font-medium text-foreground">
              {selectedProfile.name || selectedProfile.iccid}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3 text-sm">
              <div>
                <div className="text-muted-foreground">Group</div>
                <div className="font-medium text-foreground truncate">
                  {selectedProfile.groupName || 'No group'}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Network</div>
                <div className="font-medium text-foreground truncate">
                  {selectedProfile.networkName || '—'}
                </div>
              </div>
              {selectedProfile.iccid && (
                <div className="col-span-2">
                  <div className="text-muted-foreground">ICCID</div>
                  <div className="font-mono text-xs text-foreground break-all">
                    {selectedProfile.iccid}
                  </div>
                </div>
              )}
            </div>
            {/* Where this SIM lives vs. where the test was asked to run. A
                `blocking` verdict means the backend would hard-fail the run. */}
            {placement?.message && (
              <div
                className={`flex items-start gap-2 mt-2 text-sm ${
                  placement.blocking
                    ? 'text-destructive'
                    : placement.status === 'runs_in_place'
                      ? 'text-muted-foreground'
                      : 'text-amber-500'
                }`}
              >
                {placement.status === 'runs_in_place' ? (
                  <Check className="w-4 h-4 shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                )}
                <span>{placement.message}</span>
              </div>
            )}
            {placement?.holderImei && selectedProfile.shared && !placement.blocking && (
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <span>
                  Shared SIMs are pinned to their holder modem, so a forced network type that modem
                  can't do will fail the run.
                </span>
              </div>
            )}
            {/* Only `in_use` means "busy right now". `downloaded` just means the
                profile is already installed on an eUICC — that's the reuse path,
                not a problem. */}
            {selectedProfile.status === 'in_use' && (
              <div className="flex items-center gap-2 mt-2 text-sm text-amber-500">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>This SIM is currently in use — tests will queue until it frees up.</span>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Third Column: Step 3 — Network Constraints (optional) */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold bg-muted text-muted-foreground border border-dashed border-border">
            3
          </div>
          <div className="flex flex-col">
            <Label className="text-base font-semibold text-foreground">Network Constraints</Label>
            <span className="text-xs text-muted-foreground">
              All optional — the eSIM's configured APN is used unless overridden
            </span>
          </div>
        </div>

        {/* APN override (issue #161) — empty means "use the APN configured on
            the eSIM" (profile → group → network default). A typed value
            overrides it for every test + TLAM in this wizard. */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Label htmlFor="apn-input" className="text-sm text-muted-foreground">
              APN override (optional)
            </Label>
          </div>
          <input
            id="apn-input"
            type="text"
            value={apn}
            onChange={(e) => onApnChange(e.target.value)}
            placeholder={
              inheritedApn
                ? `Using "${inheritedApn}" from the selected eSIM — leave blank to keep`
                : 'Leave blank to use the eSIM’s APN'
            }
            maxLength={63}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <span className="block mt-1 text-xs text-muted-foreground">
            {apn.trim()
              ? 'Overrides the APN configured on the eSIM for data tests, Speed Test, and Test Like A Mobile.'
              : inheritedApn
                ? `Tests will use "${inheritedApn}" (set on ${inheritedApnSource}${
                    selectedProfile?.apn ? '' : '; a profile-level APN still wins'
                  }).`
                : 'Tests use the APN configured on the allocated eSIM profile / group / network, falling back to "internet".'}
          </span>
        </div>

        {/* Force-RAT selector — appears when at least one location is selected. */}
        {selectedLocationIds.length > 0 ? (
          <RatSelector
            value={selectedRat}
            onChange={onRatChange}
            availableRats={availableRats}
            availabilityByRat={availabilityByRat}
            disabled={loadingNetworks}
          />
        ) : (
          <div className="text-sm text-muted-foreground p-4 bg-muted/40 rounded-lg border border-dashed border-border">
            Select a test location first.
          </div>
        )}

        {/* Network (PLMN) selector — rendered as soon as at least one location
            is selected. Falls back to just "Automatic" when no scan results
            exist so users see the control instead of wondering where it went. */}
        {selectedLocationIds.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Radio className="w-4 h-4 text-muted-foreground" />
              <Label className="text-sm text-muted-foreground">Network (optional)</Label>
            </div>
            <select
              id="network-select"
              value={selectedNetworkMccMnc || ''}
              onChange={(e) => onNetworkChange(e.target.value || null)}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              disabled={loadingNetworks}
            >
              <option value="">Automatic (default)</option>
              {availableNetworks.map((net) => (
                <option key={`${net.mcc}${net.mnc}`} value={`${net.mcc}${net.mnc}`}>
                  {/* No RAT/generation suffix: network_generation came from the modem's
                      last scan and is often stale/misleading (e.g. Cellcom shown as "3G").
                      Operator + MCC/MNC is the stable, unambiguous label. */}
                  {net.operator_name} ({net.mcc}/{net.mnc})
                </option>
              ))}
            </select>
            {!loadingNetworks && availableNetworks.length === 0 && (
              <span className="block mt-1 text-xs text-muted-foreground">
                No scanned networks available at this location yet — runs will use the carrier's default.
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
